/**
 * Lead Sources Report
 *
 * KPIs: total leads, converted leads, conversion rate, revenue from leads
 * Chart: bar chart — lead count per source
 * Table: source breakdown with conversion % and revenue
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Users, CheckCircle2, Percent, DollarSign,
  ArrowLeft, RefreshCw, AlertCircle, MapPin,
} from 'lucide-react';
import { Button }      from '../components/ui/button';
import { toast }       from 'sonner';
import axios           from 'axios';
import ChartShell      from '../features/reports/components/ChartShell';
import KpiCard         from '../features/reports/components/KpiCard';
import PeriodSelector  from '../features/reports/components/PeriodSelector';

const API = process.env.REACT_APP_BACKEND_URL;

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

const fmtFull = (n) =>
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Consistent color palette for source bars
const SOURCE_COLORS = [
  '#f26b1d', '#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#ef4444', '#14b8a6', '#ec4899',
];

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      <p style={{ color: payload[0]?.fill }}>{payload[0]?.value} leads</p>
    </div>
  );
};

// ── Conversion rate pill ──────────────────────────────────────────────────────

const ConvPill = ({ rate }) => {
  const cls =
    rate >= 60 ? 'bg-green-100 text-green-700' :
    rate >= 30 ? 'bg-amber-100 text-amber-700' :
                 'bg-red-100 text-red-600';
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {rate}%
    </span>
  );
};

// ── Source table ──────────────────────────────────────────────────────────────

const SourceTable = ({ sources }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="lead-sources-table">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 pr-4">
            Source
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Leads
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Converted
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">
            Conv %
          </th>
          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider py-2 pl-3">
            Revenue
          </th>
        </tr>
      </thead>
      <tbody>
        {sources.map((row, i) => (
          <tr
            key={row.source}
            className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
            data-testid={`lead-source-row-${row.source.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <td className="py-2.5 pr-4">
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }}
                />
                <span className="font-medium text-gray-800">{row.source}</span>
              </div>
            </td>
            <td className="text-right text-gray-700 font-semibold py-2.5 px-3">
              {row.leads}
            </td>
            <td className="text-right text-gray-500 py-2.5 px-3">
              {row.converted}
            </td>
            <td className="text-right py-2.5 px-3">
              <ConvPill rate={row.conversionRate} />
            </td>
            <td className="text-right font-semibold text-gray-800 py-2.5 pl-3">
              {row.revenue > 0 ? fmtFull(row.revenue) : <span className="text-gray-300">—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const ReportsLeadSources = () => {
  const [period,  setPeriod]  = useState('this_month');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res   = await axios.get(`${API}/api/reports/lead-sources?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load lead sources report';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const periodLabel = {
    this_month: 'This Month',
    last_month: 'Last Month',
    last_3m:    'Last 3 Months',
    last_6m:    'Last 6 Months',
    this_year:  'This Year',
  }[period];

  // Top converting source (at least 1 lead)
  const topSource = data?.sources?.filter(s => s.leads > 0)
    .reduce((best, s) => (!best || s.conversionRate > best.conversionRate ? s : best), null);

  const isEmpty = !loading && (data?.totalLeads ?? 0) === 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">

      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <Link
            to="/reports"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Reports
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-bold text-gray-900">Lead Source Performance</h1>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(period)}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="lead-sources-kpi-grid">
        <KpiCard
          label={`Total Leads — ${periodLabel}`}
          value={loading ? '…' : String(data?.totalLeads ?? 0)}
          sub="New leads in period"
          icon={Users}
          iconColor="text-blue-500"
          iconBg="bg-blue-50"
          loading={loading}
          testId="kpi-total-leads"
        />
        <KpiCard
          label="Converted Leads"
          value={loading ? '…' : String(data?.convertedLeads ?? 0)}
          sub="Approved, booked, or completed"
          icon={CheckCircle2}
          iconColor="text-green-600"
          iconBg="bg-green-50"
          loading={loading}
          testId="kpi-converted-leads"
        />
        <KpiCard
          label="Conversion Rate"
          value={loading ? '…' : `${data?.conversionRate ?? 0}%`}
          sub="Converted ÷ total leads"
          icon={Percent}
          iconColor="text-orange-500"
          iconBg="bg-orange-50"
          loading={loading}
          testId="kpi-conversion-rate"
        />
        <KpiCard
          label="Revenue from Leads"
          value={loading ? '…' : fmtFull(data?.revenueFromLeads ?? 0)}
          sub="Paid invoices for period leads"
          icon={DollarSign}
          iconColor="text-violet-500"
          iconBg="bg-violet-50"
          loading={loading}
          testId="kpi-revenue-from-leads"
        />
      </div>

      {/* Top converting source callout */}
      {!loading && topSource && topSource.conversionRate > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm"
          data-testid="top-source-callout"
        >
          <MapPin className="w-4 h-4 text-green-600 shrink-0" />
          <span className="text-green-800">
            <span className="font-semibold">{topSource.source}</span> is your best-converting
            source this period at <span className="font-semibold">{topSource.conversionRate}%</span>
            {topSource.revenue > 0 && (
              <span> ({fmtFull(topSource.revenue)} revenue)</span>
            )}.
          </span>
        </div>
      )}

      {/* Bar chart */}
      <ChartShell
        title="Leads by Source"
        subtitle={`New leads per acquisition channel — ${periodLabel}`}
        loading={loading}
        empty={isEmpty}
      >
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={data?.bySource ?? []}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="source"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="leads" name="Leads" radius={[4, 4, 0, 0]} maxBarSize={56}>
              {(data?.bySource ?? []).map((_, i) => (
                <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>

      {/* Source breakdown table */}
      {!loading && !isEmpty && data?.sources?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Source Breakdown</h3>
          <p className="text-xs text-gray-400 mb-4">
            Conversion = leads reaching Approved, Booked, or Completed stage
          </p>
          <SourceTable sources={data.sources} />
        </div>
      )}

      {isEmpty && (
        <div className="text-center py-12 text-gray-400" data-testid="lead-sources-empty">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No leads found for this period.</p>
          <p className="text-xs mt-1">Lead source data is captured when a lead is created.</p>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

export default ReportsLeadSources;
